import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import { debounce } from 'lodash-es'
import { api } from 'components/api'
import { reviewMicroUpdatePath } from 'components/routes/path'
import SharedSupplierSearch from 'components/search/SharedSupplierSearch'
import type { SupplierSearchResult, KeyboardSelection, SearchResultsComponentProps } from 'components/search/SharedSupplierSearch'
import SearchIcon from 'components/search/SearchIcon'

interface RemoveButtonProps {
  onDismiss: () => void
}

const RemoveButton = React.forwardRef<HTMLButtonElement, RemoveButtonProps>(({ onDismiss }, ref) => (
  <button ref={ref} type='button' className='remove' aria-label='Remove' onClick={onDismiss}>
    <i className='fas fa-times' />
  </button>
))
RemoveButton.displayName = 'RemoveButton'

// Renders a styled dismissable supplier name to be used for the review
interface SupplierNameForReviewProps {
  supplierName: string
  onDismiss: () => void
  removeButtonRef?: React.Ref<HTMLButtonElement>
}

const SupplierNameForReview: React.FC<SupplierNameForReviewProps> = ({ supplierName, onDismiss, removeButtonRef }) => (
  <div className='input-group-search'>
    <SearchIcon />
    <div className='input-group-field review-supplier-selected-name'>
      <div className='selection'>
        <div className='name'>
          {supplierName}
        </div>

        <RemoveButton ref={removeButtonRef} onDismiss={onDismiss} />
      </div>
    </div>
  </div>
)

const EmptyInput: React.FC = () => null

// Clickable component for creating a new supplier for the review
interface CreateSupplierProps {
  supplierName: string
  handleMouseDown: React.MouseEventHandler<HTMLDivElement>
  hidden?: boolean
  isActive?: boolean
}

const CreateSupplier: React.FC<CreateSupplierProps> = ({ supplierName, handleMouseDown, hidden, isActive }) => {
  const ref = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (isActive === true && ref.current != null) {
      ref.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [isActive])

  if (hidden === true) { return null }
  const className = `create-supplier search-result clearfix${isActive === true ? ' active' : ''}`
  return (
    <div
      ref={ref}
      className={className}
      id='create-supplier-option'
      role='option'
      aria-selected={isActive}
      tabIndex={-1}
      onMouseDown={handleMouseDown}
    >
      <div className='new-supplier-name'>{supplierName}</div>
      <div className='float-left'>Create a business with this name &rarr;</div>
      <div className='float-right'>
        <i className='fas fa-plus' />
      </div>
    </div>
  )
}

interface SearchResultProps {
  result: SupplierSearchResult
  handleMouseDown: React.MouseEventHandler<HTMLDivElement>
  isActive?: boolean
}

const SearchResult: React.FC<SearchResultProps> = ({ result, handleMouseDown, isActive }) => {
  const ref = React.useRef<HTMLDivElement>(null)
  const className = `search-result${isActive === true ? ' active' : ''}`

  React.useEffect(() => {
    if (isActive === true && ref.current != null) {
      ref.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [isActive])

  return (
    <div
      ref={ref}
      className={className}
      key={result.id}
      id={`supplier-search-${result.id}`}
      role='option'
      aria-selected={isActive}
      tabIndex={-1}
      onMouseDown={handleMouseDown}
    >
      <div className='name'>{result.name}</div>
      <div>{result.location}</div>
    </div>
  )
}

interface SearchResultsInternalProps {
  searchResults: SupplierSearchResult[] | null
  supplierName: string
  handleSearchResult: (result: SupplierSearchResult) => React.MouseEventHandler<HTMLDivElement>
  handleSupplierName: (name: string) => React.MouseEventHandler<HTMLDivElement>
  activeIndex: number
  onMouseMove: () => void
}

const SearchResults: React.FC<SearchResultsInternalProps> = ({
  searchResults, supplierName, handleSearchResult, handleSupplierName, activeIndex, onMouseMove
}) => {
  const hasCreateOption = supplierName.length >= 3

  return (
    <div className='search-results show' role='listbox' id='supplier-search-listbox' onMouseMove={onMouseMove}>
      <CreateSupplier
        supplierName={supplierName}
        handleMouseDown={handleSupplierName(supplierName)}
        hidden={!hasCreateOption}
        isActive={hasCreateOption && activeIndex === 0}
      />
      {
        (searchResults ?? []).map((result, index) => {
          const resultIndex = hasCreateOption ? index + 1 : index
          return (
            <SearchResult
              key={result.id}
              result={result}
              handleMouseDown={handleSearchResult(result)}
              isActive={activeIndex === resultIndex}
            />
          )
        })
      }
    </div>
  )
}

// Higher order component to inject the appropriate handlers into the SearchResults component
// rendered by the SupplierSearchShared component
const searchResultsWithHandlers = (
  handleSearchResult: (result: SupplierSearchResult) => React.MouseEventHandler<HTMLDivElement>,
  handleSupplierName: (name: string) => React.MouseEventHandler<HTMLDivElement>
): React.FC<SearchResultsComponentProps> =>
  ({ searchResults, supplierName, activeIndex, onMouseMove }: SearchResultsComponentProps) =>
    SearchResults({ searchResults, supplierName, handleSearchResult, handleSupplierName, activeIndex, onMouseMove })

// poor man's enum
const SearchInputBehavior = {
  search: 'search',
  supplierName: 'supplierName',
  empty: 'empty'
} as const

type SearchInputBehaviorType = typeof SearchInputBehavior[keyof typeof SearchInputBehavior]

interface SearchSuppliersProps {
  supplierName?: string
  supplierId?: string
  reviewId: number
}

interface MicroUpdateResponse {
  supplier?: string | null
  contract_vehicles?: unknown
}

const SearchSuppliers: React.FC<SearchSuppliersProps> = ({ supplierName: initialSupplierName, supplierId: initialSupplierId, reviewId }) => {
  const [supplierName, setSupplierName] = useState(initialSupplierName ?? '')
  const [supplierId, setSupplierId] = useState(initialSupplierId ?? '')
  const [searchInputBehavior, setSearchInputBehavior] = useState<SearchInputBehaviorType>(
    (initialSupplierName ?? '') !== '' ? SearchInputBehavior.supplierName : SearchInputBehavior.search
  )

  const removeButtonRef = useRef<HTMLButtonElement>(null)
  const prevBehaviorRef = useRef(searchInputBehavior)

  // Focus the remove button when switching from search/empty → supplierName (not on initial mount)
  useEffect(() => {
    if (
      searchInputBehavior === SearchInputBehavior.supplierName &&
      prevBehaviorRef.current !== SearchInputBehavior.supplierName
    ) {
      removeButtonRef.current?.focus()
    }
    prevBehaviorRef.current = searchInputBehavior
  }, [searchInputBehavior])

  // This method updates a hidden DOM element with the chosen supplier and the contract vehicles on which the supplier is listed.
  // The SelectContract component requires this information to function correctly i.e. allow the user to only select a contract on which the supplier is listed.
  const updateDOMForContractSelection = useCallback((data: MicroUpdateResponse): void => {
    $('#chosen-supplier-data').data({ supplier: data.supplier, contractVehicles: data.contract_vehicles })
    $('#chosen-supplier-data').attr('supplier', (data.supplier != null && data.supplier !== '') ? data.supplier : 'none') // triggers MutationListener
  }, [])

  const microUpdate = useCallback(async (data: Record<string, unknown>): Promise<void> => {
    const res = await api.patchWithCsrf<Record<string, unknown>, MicroUpdateResponse>({
      url: reviewMicroUpdatePath(reviewId),
      data
    })
    updateDOMForContractSelection(res.data)
  }, [reviewId, updateDOMForContractSelection])

  const debouncedSaveTemporarySupplierName = useMemo(
    () => debounce((name: string): void => {
      const data = { review: { flex: { temp_supplier_name: name } } }
      void microUpdate(data)
    }, 300),
    [microUpdate]
  )

  const handleSearchResult = useCallback((result: SupplierSearchResult) =>
    (_event?: React.MouseEvent): void => {
      debouncedSaveTemporarySupplierName.cancel()
      setSupplierId(result.id)
      setSupplierName(result.name ?? '')
      setSearchInputBehavior(SearchInputBehavior.supplierName)

      const data = {
        review: {
          supplier_id: result.id,
          flex: { temp_supplier_name: result.name ?? '' }
        }
      }
      void microUpdate(data)
    }, [microUpdate, debouncedSaveTemporarySupplierName])

  const handleSupplierName = useCallback((name: string): ((_event?: React.MouseEvent) => void) => {
    return (_event?: React.MouseEvent): void => {
      debouncedSaveTemporarySupplierName(name)
      setSupplierName(name)
      setSearchInputBehavior(SearchInputBehavior.supplierName)
    }
  }, [debouncedSaveTemporarySupplierName])

  const handleRemoveSupplier = useCallback((): void => {
    debouncedSaveTemporarySupplierName.cancel()
    setSupplierId('')
    setSupplierName('')
    setSearchInputBehavior(SearchInputBehavior.search)
    const data = { review: { supplier_id: '', flex: { temp_supplier_name: '' } } }
    void microUpdate(data)
  }, [microUpdate, debouncedSaveTemporarySupplierName])

  const handleKeyboardSelect = useCallback((selection: KeyboardSelection): void => {
    if (selection.type === 'create') {
      handleSupplierName(selection.supplierName)()
    } else if (selection.type === 'select') {
      handleSearchResult(selection.result)()
    }
  }, [handleSearchResult, handleSupplierName])

  const handleBlur = useCallback((): void => {
    setSearchInputBehavior((prev) => prev !== SearchInputBehavior.supplierName ? SearchInputBehavior.empty : prev)
  }, [])

  const handleFocus = useCallback((): void => {
    setSearchInputBehavior((prev) => prev !== SearchInputBehavior.supplierName ? SearchInputBehavior.search : prev)
  }, [])

  const renderContent = (): React.ReactNode => {
    switch (searchInputBehavior) {
      case SearchInputBehavior.supplierName:
        return (
          <SupplierNameForReview
            supplierName={supplierName}
            onDismiss={handleRemoveSupplier}
            removeButtonRef={removeButtonRef}
          />
        )
      case SearchInputBehavior.search:
        return (
          <SharedSupplierSearch
            SearchResultsComponent={searchResultsWithHandlers(handleSearchResult, handleSupplierName)}
            onKeyboardSelect={handleKeyboardSelect}
            required
          />
        )
      case SearchInputBehavior.empty:
        return (
          <SharedSupplierSearch
            SearchResultsComponent={EmptyInput}
            required
          />
        )
    }
  }

  return (
    <div className='supplier-search' role='group' aria-labelledby='supplier-label' aria-describedby='supplier-error'>
      <label
        id='supplier-label'
        className='select optional disabled sub-header'
        htmlFor='supplier-input'
      >
        Supplier Business Name<span className='text-danger'>*</span>
      </label>

      <div onBlur={handleBlur} onFocus={handleFocus}>
        {renderContent()}
      </div>

      <input type='hidden' name='review[supplier_id]' value={supplierId} />
      <input type='hidden' name='review[flex][temp_supplier_name]' value={supplierName} />
    </div>
  )
}

export default SearchSuppliers
